import { useState } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input,
  Button, Row, Col, Table, Badge, Alert, Spinner,
} from 'reactstrap';
import { HasPermission } from '../../rbac/HasPermission';
import {
  useApiCatalogDetail, useCreateCatalog, useUpdateCatalog,
  useUpdateTier, useAddEndpoint, useDeleteEndpoint,
} from './useApiCatalog';

const EMPTY_META = {
  slug: '', name: '', tagline: '', category: '', base_path: '',
  upstream_url: '', openapi_url: '', docs_url: '', description_md: '',
  is_public: true, sort: 100,
};

/**
 * #151 Katalog-Eintrag anlegen/bearbeiten: Meta-Daten, Preis-Tarife und
 * Endpoint-Doku. catalogId=null → Neuanlage (nur Meta; Tarife/Endpoints erst
 * nach dem Speichern verfügbar).
 */
export function ApiCatalogModal({ isOpen, catalogId, onClose }) {
  const isEdit = !!catalogId;
  const detailQ = useApiCatalogDetail(catalogId);
  const detail = detailQ.data?.catalog;

  const create = useCreateCatalog();
  const update = useUpdateCatalog();
  const busy = create.isPending || update.isPending;

  const [meta, setMeta] = useState(EMPTY_META);

  // Detail lädt asynchron NACH Mount → Form beim ersten Eintreffen einmalig
  // aus den Serverdaten seeden. Bewusst im Render (nicht im Effect) — das ist
  // das dokumentierte „State an geänderte Props anpassen"-Muster und vermeidet
  // die vom react-compiler-strict verbotene setState-im-Effect-Kaskade.
  const [seededFor, setSeededFor] = useState(null);
  if (isEdit && detail && seededFor !== detail.id) {
    setSeededFor(detail.id);
    setMeta({
      slug: detail.slug ?? '', name: detail.name ?? '', tagline: detail.tagline ?? '',
      category: detail.category ?? '', base_path: detail.basePath ?? '',
      upstream_url: detail.upstreamUrl ?? '', openapi_url: detail.openapiUrl ?? '',
      docs_url: detail.docsUrl ?? '', description_md: detail.descriptionMd ?? '',
      is_public: detail.isPublic !== false, sort: detail.sort ?? 100,
    });
  }

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setMeta((prev) => ({ ...prev, [k]: v }));
  };

  const onSaveMeta = async (e) => {
    e.preventDefault();
    if (isEdit) {
      const { slug: _slug, ...patch } = meta;   // slug ist immutable
      await update.mutateAsync({ id: catalogId, patch });
    } else {
      await create.mutateAsync(meta);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>
        {isEdit ? `API bearbeiten · ${meta.name || meta.slug}` : 'Neue API im Katalog'}
      </ModalHeader>
      <ModalBody>
        {isEdit && detailQ.isLoading && <div className="text-center py-4"><Spinner /></div>}

        <Form onSubmit={onSaveMeta}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label>Slug</Label>
                <Input value={meta.slug} onChange={set('slug')} disabled={isEdit}
                  placeholder="events" required />
                {isEdit && <small className="text-muted">Slug ist unveränderlich.</small>}
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label>Name</Label>
                <Input value={meta.name} onChange={set('name')} placeholder="Events API" required />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Tagline</Label>
            <Input value={meta.tagline} onChange={set('tagline')} />
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Kategorie</Label>
                <Input value={meta.category} onChange={set('category')} placeholder="Daten" />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Sortierung</Label>
                <Input type="number" value={meta.sort} onChange={set('sort')} />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup check className="mt-4 pt-2">
                <Input type="checkbox" id="ispublic" checked={meta.is_public} onChange={set('is_public')} />
                <Label check for="ispublic">Öffentlich sichtbar</Label>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Basispfad (Gateway)</Label>
                <Input value={meta.base_path} onChange={set('base_path')} placeholder="/v1/events" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Upstream-URL (Tester)</Label>
                <Input value={meta.upstream_url} onChange={set('upstream_url')}
                  placeholder="http://events-api.valitype.svc.cluster.local:3007" />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>OpenAPI-URL</Label>
                <Input value={meta.openapi_url} onChange={set('openapi_url')} placeholder="/v1/openapi" />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Docs-URL</Label>
                <Input value={meta.docs_url} onChange={set('docs_url')} />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Beschreibung (Markdown)</Label>
            <Input type="textarea" rows="3" value={meta.description_md} onChange={set('description_md')} />
          </FormGroup>
          <HasPermission perms={['products.write']}>
            <Button type="submit" color="primary" disabled={busy}>
              {busy ? 'Speichert…' : (isEdit ? 'Meta-Daten speichern' : 'API anlegen')}
            </Button>
          </HasPermission>
        </Form>

        {isEdit && detail && (
          <>
            <hr />
            <TierEditor catalogId={catalogId} tiers={detail.tiers ?? []} />
            <hr />
            <EndpointEditor catalogId={catalogId} endpoints={detail.endpoints ?? []} />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>Schließen</Button>
      </ModalFooter>
    </Modal>
  );
}

// ---- Tarife -----------------------------------------------------------------

function TierEditor({ catalogId, tiers }) {
  return (
    <div>
      <h6>Tarife &amp; Preise</h6>
      <p className="small text-muted">
        Preise gelten für neue Buchungen. Bestehende Abos behalten ihren Snapshot-Preis
        bis zur nächsten Verlängerung.
      </p>
      {tiers.length === 0 ? (
        <Alert color="light" className="small">
          Keine Tarife. Tarife sind CORE-Produkte mit <code>driver='api'</code> und
          {' '}<code>driver_config.api_slug</code> = Slug dieser API — via „Produkte" anlegen.
        </Alert>
      ) : (
        <Table size="sm" responsive className="align-middle">
          <thead>
            <tr>
              <th>Tarif</th>
              <th style={{ width: 120 }}>Preis (€/M)</th>
              <th style={{ width: 130 }}>Inkl. Requests</th>
              <th style={{ width: 130 }}>Overage /1000 (ct)</th>
              <th style={{ width: 100 }}>RPS</th>
              <th style={{ width: 90 }}>Aktiv</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {tiers.map((t) => <TierRow key={t.id} catalogId={catalogId} tier={t} />)}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function TierRow({ tier }) {
  const update = useUpdateTier();
  const [form, setForm] = useState({
    eur: (tier.unitAmount / 100).toFixed(2),
    includedRequests: tier.includedRequests ?? '',
    overagePer1000Cents: tier.overagePer1000Cents ?? '',
    rateLimitRps: tier.rateLimitRps ?? '',
    isActive: tier.isActive !== false,
  });
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: v }));
  };
  const onSave = async () => {
    const patch = {
      unitAmount: Math.round(parseFloat(form.eur || '0') * 100),
      includedRequests: form.includedRequests === '' ? null : Number(form.includedRequests),
      overagePer1000Cents: form.overagePer1000Cents === '' ? null : Number(form.overagePer1000Cents),
      rateLimitRps: form.rateLimitRps === '' ? null : Number(form.rateLimitRps),
      isActive: form.isActive,
    };
    await update.mutateAsync({ productId: tier.id, patch });
  };
  return (
    <tr>
      <td>
        <div className="fw-semibold">{tier.tier || tier.sku}</div>
        <div className="small text-muted">{tier.name}</div>
      </td>
      <td><Input bsSize="sm" type="number" step="0.01" value={form.eur} onChange={set('eur')} /></td>
      <td><Input bsSize="sm" type="number" value={form.includedRequests} onChange={set('includedRequests')} /></td>
      <td><Input bsSize="sm" type="number" value={form.overagePer1000Cents} onChange={set('overagePer1000Cents')} /></td>
      <td><Input bsSize="sm" type="number" value={form.rateLimitRps} onChange={set('rateLimitRps')} /></td>
      <td>
        <Input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
      </td>
      <td>
        <HasPermission perms={['products.write']}>
          <Button size="sm" color="primary" outline disabled={update.isPending} onClick={onSave}>
            {update.isPending ? '…' : 'Speichern'}
          </Button>
        </HasPermission>
      </td>
    </tr>
  );
}

// ---- Endpoints --------------------------------------------------------------

function EndpointEditor({ catalogId, endpoints }) {
  const add = useAddEndpoint();
  const del = useDeleteEndpoint();
  const [form, setForm] = useState({ method: 'GET', path: '', summary: '', sample_query: '' });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const onAdd = async (e) => {
    e.preventDefault();
    if (!form.path.trim()) return;
    await add.mutateAsync({ id: catalogId, body: form });
    setForm({ method: 'GET', path: '', summary: '', sample_query: '' });
  };
  return (
    <div>
      <h6>Endpoint-Doku</h6>
      <Table size="sm" responsive className="align-middle">
        <thead>
          <tr>
            <th style={{ width: 70 }}>Methode</th>
            <th>Pfad</th>
            <th>Beschreibung</th>
            <th style={{ width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {endpoints.map((ep) => (
            <tr key={ep.id}>
              <td><Badge color="info">{ep.method}</Badge></td>
              <td><code className="small">{ep.path}</code></td>
              <td className="small">{ep.summary}</td>
              <td>
                <HasPermission perms={['products.write']}>
                  <Button size="sm" color="danger" outline disabled={del.isPending}
                    onClick={() => del.mutateAsync({ id: catalogId, eid: ep.id })}>×</Button>
                </HasPermission>
              </td>
            </tr>
          ))}
          {endpoints.length === 0 && (
            <tr><td colSpan="4" className="text-muted small">Noch keine Endpoints dokumentiert.</td></tr>
          )}
        </tbody>
      </Table>
      <HasPermission perms={['products.write']}>
        <Form onSubmit={onAdd}>
          <Row className="g-2 align-items-end">
            <Col md="2">
              <Label className="small mb-0">Methode</Label>
              <Input type="select" bsSize="sm" value={form.method} onChange={set('method')}>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}
              </Input>
            </Col>
            <Col md="4">
              <Label className="small mb-0">Pfad</Label>
              <Input bsSize="sm" value={form.path} onChange={set('path')} placeholder="/v1/events" />
            </Col>
            <Col md="4">
              <Label className="small mb-0">Beschreibung</Label>
              <Input bsSize="sm" value={form.summary} onChange={set('summary')} />
            </Col>
            <Col md="2">
              <Button type="submit" size="sm" color="primary" disabled={add.isPending} block>
                Hinzufügen
              </Button>
            </Col>
          </Row>
        </Form>
      </HasPermission>
    </div>
  );
}
